import { teamLabel } from '@/lib/labels';

/**
 * Teams with their own admin section.
 *
 * Their invites, outreachers, payroll and performance live under
 * /admin/<section>/…, pinned to the team (TeamScopeLock in router.tsx). The
 * section's routes and nav are in router.tsx and AdminLayout.tsx. Whether a
 * team also shows in the default admin views is DEFAULT_EXCLUDED_TEAMS.
 */
export interface TeamSection {
  team: string;
  label: string;
  /** Route prefix, e.g. /admin/influencer. */
  base: string;
}

export const TEAM_SECTIONS: readonly TeamSection[] = [
  { team: 'INFLUENCER', label: 'Influencer', base: '/admin/influencer' },
  { team: 'VYRAL', label: 'Vyral', base: '/admin/vyral' },
];

/**
 * Teams the default (no team picked) admin views leave out, unless one is
 * picked in the header's team switcher. The default is Social Media + Vyral:
 * Vyral has its own section and shows here too.
 */
export const DEFAULT_EXCLUDED_TEAMS: readonly string[] = ['INFLUENCER', 'DISCORD'];

export function teamSection(code: string | null | undefined): TeamSection | undefined {
  return code ? TEAM_SECTIONS.find((s) => s.team === code) : undefined;
}

/** The section a path belongs to, e.g. /admin/vyral/payroll → Vyral. */
export function sectionForPath(pathname: string): TeamSection | undefined {
  return TEAM_SECTIONS.find((s) => pathname === s.base || pathname.startsWith(`${s.base}/`));
}

/** Where a team's invites live: its own section, or the main Invites page. */
export function invitesBaseForTeam(code: string | null | undefined): string {
  const section = teamSection(code);
  return section ? `${section.base}/invites` : '/admin/invites';
}

/**
 * The teams the default view leaves out, with the Invites list each one is
 * on: its own section, or the main list with the team picked.
 */
export const DEFAULT_EXCLUDED_LINKS: readonly { team: string; label: string; base: string; pick: boolean }[] =
  DEFAULT_EXCLUDED_TEAMS.map((team) => ({
    team,
    label: teamLabel(team),
    base: invitesBaseForTeam(team),
    /** No section: the link has to carry `?team=`. */
    pick: !teamSection(team),
  }));

export interface TeamScopeParams {
  team?: string;
  /** Comma list, as the backend's `excludeTeam` param takes it. */
  excludeTeam?: string;
}

/**
 * API params for a team scope: `team` when one is in scope, otherwise the
 * excluded teams as `excludeTeam=A,B`. Empty when neither applies.
 */
export function teamScopeParams(scope: { team: string; excludeTeams: readonly string[] }): TeamScopeParams {
  if (scope.team) return { team: scope.team };
  return scope.excludeTeams.length > 0 ? { excludeTeam: scope.excludeTeams.join(',') } : {};
}

/** Rows (payroll, …) for a team scope, for endpoints that return every team. */
export function rowsInScope<T extends { team: string }>(
  rows: readonly T[],
  scope: { team: string; excludeTeams: readonly string[] },
): T[] {
  if (scope.team) return rows.filter((r) => r.team === scope.team);
  if (scope.excludeTeams.length === 0) return [...rows];
  return rows.filter((r) => !scope.excludeTeams.includes(r.team));
}

/**
 * Name of the default (no team picked) scope: every active team it doesn't
 * leave out, e.g. "Social Media + Vyral". Not "All teams" — it isn't.
 */
export function mainScopeLabel(
  teams?: readonly { code: string; displayName?: string | null; isActive?: boolean }[] | null,
): string {
  const names = (teams ?? [])
    .filter((t) => t.isActive !== false && !DEFAULT_EXCLUDED_TEAMS.includes(t.code))
    .map((t) => teamLabel(t.code, t.displayName));
  return names.length > 0 ? names.join(' + ') : 'Social Media + Vyral';
}
